import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Gauge, Thermometer, Map, Smartphone, Database, Monitor } from 'lucide-react';

const SmartEnergy = () => {
  const solutions = [
    {
      icon: Database,
      title: 'Transformer Digitization',
      description: 'IoT sensor integration for real-time monitoring of critical electrical infrastructure parameters.',
      features: [
        'Current monitoring sensors',
        'Voltage measurement systems',
        'Temperature sensors for thermal monitoring',
        'Oil level and quality sensors',
        'Vibration and acoustic monitoring'
      ]
    },
    {
      icon: Map,
      title: 'GIS Layer Integration',
      description: 'Geographic Information System mapping of all utility assets for spatial analysis and visualization.',
      features: [
        'Geo-tagged asset locations',
        'Interactive map visualization',
        'Network topology mapping',
        'Route optimization for maintenance',
        'Spatial analytics for planning'
      ]
    },
    {
      icon: Monitor,
      title: 'Web Portal',
      description: 'Comprehensive web-based dashboard for monitoring, analytics, and management of digitized utility assets.',
      features: [
        'Real-time asset health monitoring',
        'Alert and notification system',
        'Historical data analytics',
        'Predictive maintenance scheduling',
        'Report generation and export',
        'User access management'
      ]
    },
    {
      icon: Smartphone,
      title: 'Mobile Application',
      description: 'Field-ready mobile app enabling technicians to digitize assets on-site with location, images, and detailed specifications.',
      features: [
        'GPS-based location capture',
        'Photo documentation',
        'Asset detail form entry',
        'Barcode/QR code scanning',
        'Offline mode capability',
        'Instant sync with central database'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Real-Time Monitoring',
      description: 'Continuous tracking of current, voltage, and temperature prevents failures and enables proactive maintenance.',
      icon: Gauge
    },
    {
      title: 'Predictive Maintenance',
      description: 'AI-powered analytics predict equipment failures before they occur, reducing downtime and maintenance costs.',
      icon: Zap
    },
    {
      title: 'Asset Visibility',
      description: 'Complete digital inventory of all utility assets with location, specifications, and operational history.',
      icon: Map
    },
    {
      title: 'Improved Safety',
      description: 'Temperature monitoring and anomaly detection prevent hazardous situations and ensure worker safety.',
      icon: Thermometer
    }
  ];

  return (
    <div className="detail-page">
      <div className="detail-header-section">
        <div className="detail-header-overlay"></div>
        <img
          src="https://images.unsplash.com/photo-1613665813446-82a78c468a1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxzb2xhciUyMGVuZXJneXxlbnwwfHx8fDE3NzE3Mjg5OTZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Smart Energy"
          className="detail-header-image"
        />
        <div className="detail-header-content">
          <Link to="/" className="back-button-detail">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 className="detail-title">Smart Energy</h1>
          <p className="detail-subtitle">
            Digitizing transformers and utility assets for intelligent grid management
          </p>
        </div>
      </div>

      <div className="detail-container">
        <section className="detail-section">
          <h2 className="detail-section-title">Solution Overview</h2>
          <div className="project-overview">
            <p className="project-description">
              Our Smart Energy solution transforms traditional utility infrastructure into an intelligent, 
              connected ecosystem. By digitizing transformers and utility assets with IoT sensors, we enable 
              real-time monitoring of critical parameters like current, voltage, and temperature. The solution 
              includes GIS-based asset mapping, a comprehensive web portal for analytics, and a mobile 
              application for field digitization—creating a complete digital twin of your energy infrastructure.
            </p>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Solution Components</h2>
          <div className="systems-grid">
            {solutions.map((solution, index) => (
              <div key={index} className="system-card">
                <div className="system-header">
                  <div className="system-icon-wrapper">
                    <solution.icon size={28} />
                  </div>
                  <h3 className="system-title">{solution.title}</h3>
                </div>
                <p className="system-description">{solution.description}</p>
                <ul className="system-features">
                  {solution.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Key Benefits</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <benefit.icon className="benefit-icon" size={32} />
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Sensor-Based Monitoring</h2>
          <div className="monitoring-grid">
            <div className="monitoring-card">
              <Zap size={40} className="monitoring-icon" />
              <h4>Current Monitoring</h4>
              <p>Real-time current measurement across all phases with configurable alerts for overload conditions and anomalies.</p>
            </div>
            <div className="monitoring-card">
              <Gauge size={40} className="monitoring-icon" />
              <h4>Voltage Tracking</h4>
              <p>Continuous voltage monitoring to detect fluctuations, under-voltage, and over-voltage conditions affecting grid stability.</p>
            </div>
            <div className="monitoring-card">
              <Thermometer size={40} className="monitoring-icon" />
              <h4>Temperature Sensing</h4>
              <p>Thermal monitoring of transformers and equipment to prevent overheating and predict maintenance needs.</p>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Mobile Asset Digitization</h2>
          <div className="architecture-content">
            <p>
              Our mobile application empowers field technicians to digitize utility assets on-site with 
              comprehensive data capture capabilities:
            </p>
            <div className="mobile-features-grid">
              <div className="mobile-feature">
                <h4>Location Capture</h4>
                <p>GPS coordinates automatically recorded for precise asset positioning on GIS maps</p>
              </div>
              <div className="mobile-feature">
                <h4>Photo Documentation</h4>
                <p>High-resolution images of assets, nameplates, and installation conditions</p>
              </div>
              <div className="mobile-feature">
                <h4>Asset Details</h4>
                <p>Structured forms for capturing specifications, ratings, manufacturer info, and installation date</p>
              </div>
              <div className="mobile-feature">
                <h4>Instant Sync</h4>
                <p>Real-time synchronization with central database and web portal for immediate visibility</p>
              </div>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Implementation Outcomes</h2>
          <div className="outcomes-list">
            <div className="outcome-item">
              <div className="outcome-number">01</div>
              <div className="outcome-content">
                <h4>Complete Asset Visibility</h4>
                <p>Digital inventory of all transformers and utility assets with real-time health status</p>
              </div>
            </div>
            <div className="outcome-item">
              <div className="outcome-number">02</div>
              <div className="outcome-content">
                <h4>Reduced Downtime</h4>
                <p>Predictive maintenance based on sensor data minimizes unplanned outages</p>
              </div>
            </div>
            <div className="outcome-item">
              <div className="outcome-number">03</div>
              <div className="outcome-content">
                <h4>Operational Efficiency</h4>
                <p>GIS-based route optimization and mobile tools accelerate field operations</p>
              </div>
            </div>
            <div className="outcome-item">
              <div className="outcome-number">04</div>
              <div className="outcome-content">
                <h4>Data-Driven Decisions</h4>
                <p>Analytics and insights from sensor data enable strategic infrastructure planning</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SmartEnergy;
